import {
  positionAtArrival,
  quantityBeforeCap,
  salesBeforeArrival,
  stockAtArrival,
  type MaxLimit,
} from "~/utils/allocationRuleMaths"
import type { QtyField } from "~/utils/allocationLineSettings"
import {
  EXCLUDED,
  hasMinimum,
  minDisplayQtyText,
  type EngineLevels,
  type RunLineSettings,
  type SettingSource,
} from "~/utils/suggestedQuantity"
import type { BreakdownRow } from "./BreakdownTable"
import type { CappedLevels, ReplRow } from "./buildReplenishmentRow"

// Sales in transit are ROS x days, so stock at arrival is rarely a whole number.
const units = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 })

const SOURCE_TEXT: Record<SettingSource, string> = {
  storeSku: "store × SKU setting",
  variant: "variant setting, all stores",
  default: "default",
}

const EXCLUDED_SENTENCE =
  "A store × SKU setting excludes this line from replenishment, so nothing is suggested. Stock already in the store stays."

/** The engine suggests 0 on an excluded line, but a planner's override stays (it goes into the transfer). */
export function excludedOverrideText(override: number): string {
  return `${EXCLUDED_SENTENCE} The override of ${override.toLocaleString()} stays as a manual qty and goes into the transfer.`
}

export function minimumRaisedText(mdq: number): string {
  return `The minimum display quantity of ${mdq} raises the suggested qty, so the store holds it when the delivery arrives. The rate of sale alone would suggest less.`
}

const SETTING_LABEL: Record<QtyField, string> = {
  minDisplayQty: "Minimum display quantity",
  maxQty: "Maximum stock",
  orderMultiple: "Order multiple",
}

const SOURCE_HINT: Record<SettingSource, string> = {
  storeSku: "Clear the cell to fall back to the variant setting or the default.",
  variant:
    "Clearing the cell changes nothing. Enter a value to set a store × SKU setting that overrides it.",
  default: "No setting is saved for it. Enter a value to set one for this store × SKU.",
}

/** The value the run used for one setting cell and the level it came from, for the cell tooltip. */
export function settingSourceText(field: QtyField, settings: RunLineSettings): string | null {
  const source = settings.settingSources?.[field]
  if (source == null) return null
  const value = settings[field]
  const valueText =
    field === "minDisplayQty"
      ? minDisplayQtyText(settings.minDisplayQty)
      : (value?.toLocaleString() ?? "None")
  return `${SETTING_LABEL[field]}: ${valueText}\nSource: ${SOURCE_TEXT[source]}. ${SOURCE_HINT[source]}`
}

function conflictSentence(settings: RunLineSettings): string | null {
  const conflict = settings.settingsConflict
  const sources = settings.settingSources
  if (conflict == null || sources == null) return null
  const ignored = `${conflict.ignored.value} (${SOURCE_TEXT[conflict.ignored.source]})`
  if (conflict.winner === "maxQty") {
    return `The minimum display quantity of ${ignored} is above the maximum stock of ${settings.maxQty} (${SOURCE_TEXT[sources.maxQty]}). The more specific setting wins, so there is no minimum.`
  }
  return `The minimum display quantity of ${settings.minDisplayQty} (${SOURCE_TEXT[sources.minDisplayQty]}) is above the maximum stock of ${ignored}. The more specific setting wins, so there is no maximum.`
}

type LimitedLevels = CappedLevels & { maxLimit: NonNullable<MaxLimit> }

const MAX_LIMIT_SENTENCE: Record<NonNullable<MaxLimit>, (levels: LimitedLevels) => string> = {
  atMaximum: (lv) =>
    `The inventory position of ${lv.position} already reaches the maximum stock of ${lv.maxQty}, so the suggested qty is 0.`,
  noMultipleFits: (lv) =>
    `Less than one order multiple of ${lv.orderMultiple} fits under the maximum stock of ${lv.maxQty}, so the suggested qty is 0.`,
  capped: (lv) =>
    `The order-up-to level of ${lv.orderUpTo} is above the maximum stock of ${lv.maxQty}, so the suggested qty fills up to the maximum only.`,
  cappedToMultiple: (lv) =>
    `The order-up-to level of ${lv.orderUpTo} is above the maximum stock of ${lv.maxQty}, so the suggested qty is the largest multiple of ${lv.orderMultiple} that fits under it.`,
  minimumCapped: (lv) =>
    `The maximum stock of ${lv.maxQty} cuts back the raise from the minimum display quantity of ${lv.mdq}, so the suggested qty ${
      lv.orderMultiple > 1
        ? `is the largest multiple of ${lv.orderMultiple} that fits under it`
        : "fills up to the maximum only"
    }.`,
  rounding: (lv) =>
    `Rounding up to the order multiple of ${lv.orderMultiple} would pass the maximum stock of ${lv.maxQty}, so the suggested qty is rounded down to fit the maximum.`,
}

/** What the minimum display quantity and maximum stock did to a quantity, one sentence each. */
export function limitSentences(
  levels: EngineLevels,
  { maxLimit, minimumRaised }: { maxLimit: MaxLimit; minimumRaised: boolean | null },
): string[] {
  const sentences: string[] = []
  if (minimumRaised) sentences.push(minimumRaisedText(levels.mdq))
  if (maxLimit != null && levels.maxQty != null) {
    sentences.push(MAX_LIMIT_SENTENCE[maxLimit]({ ...levels, maxQty: levels.maxQty, maxLimit }))
  }
  return sentences
}

export function belowMinimumText(levels: EngineLevels, quantity: number): string {
  const arrival = stockAtArrival(levels, quantity)
  const below = `With the final qty the store holds ${units(arrival)} when the delivery arrives, below the minimum display quantity of ${levels.mdq}.`
  const after = levels.position + quantity
  if (levels.maxQty == null || after + levels.orderMultiple <= levels.maxQty) return below
  return `${below} One more order multiple of ${levels.orderMultiple} would pass the maximum stock of ${levels.maxQty}.`
}

/** The maximum caps the suggested qty only; a planner's override above it stays. */
export function aboveMaximumText(levels: CappedLevels, quantity: number): string {
  return `${quantity.toLocaleString()} units take the store to ${(levels.position + quantity).toLocaleString()}, above its maximum stock of ${levels.maxQty.toLocaleString()}.`
}

type ReasonRow = Pick<
  ReplRow,
  "reasonCode" | "engineLevels" | "effective" | "excludedOverride" | "maximumLeavesBelowMinimum"
>

/** What the line settings did to the suggested qty, for the inspector's Rules applied card. */
export function lineSettingReasons(
  row: ReasonRow,
  settings: RunLineSettings | null | undefined,
): string[] {
  if (row.reasonCode === EXCLUDED) {
    return [
      row.excludedOverride != null ? excludedOverrideText(row.excludedOverride) : EXCLUDED_SENTENCE,
    ]
  }
  const levels = row.engineLevels
  const reasons = [settings && conflictSentence(settings)]
  if (levels) {
    reasons.push(...limitSentences(levels, levels))
    if (row.maximumLeavesBelowMinimum) reasons.push(belowMinimumText(levels, row.effective))
  }
  return reasons.filter((reason): reason is string => reason != null)
}

function signed(n: number): string {
  if (n > 0) return `+${n.toLocaleString()}`
  if (n < 0) return `−${Math.abs(n).toLocaleString()}`
  return "0"
}

const aboveReorderPoint = (levels: EngineLevels) => levels.position > levels.reorderPoint

// The maximum caps the inventory position, so it is checked before sales in transit come off.
function maximumNote(levels: EngineLevels, unrestricted: number): string {
  const { maxQty, maxLimit, position } = levels
  if (maxQty == null) return "not set"
  if (maxLimit == null) return "not passed"
  const wanted = quantityBeforeCap(unrestricted, position, levels)
  const excess = position + wanted - maxQty
  return `position + ${wanted > unrestricted ? "rounded or raised qty" : "unrestricted"} is ${excess.toLocaleString()} above, so cut`
}

function minimumNote(levels: EngineLevels, projected: number): string {
  if (!hasMinimum(levels.mdq)) return "not set"
  if (levels.minimumRaised == null) return "older run, inside the levels"
  if (levels.minimumRaised) return `${units(levels.mdq - projected)} below, so raised`
  return "reached"
}

type SuggestedQtyRow = Pick<
  ReplRow,
  "engineLevels" | "unrestrictedSuggestion" | "recommendedQuantity"
>

/**
 * The Suggested qty breakdown: where the unrestricted suggestion takes the store,
 * each product setting against it, and how much they changed it. Null without levels.
 */
export function suggestedQtyRows({
  engineLevels: levels,
  unrestrictedSuggestion: unrestricted,
  recommendedQuantity,
}: SuggestedQtyRow): BreakdownRow[] | null {
  if (levels == null || unrestricted == null) return null
  const sales = salesBeforeArrival(levels)
  const projected = positionAtArrival(levels.position, sales) + unrestricted
  const change = recommendedQuantity - unrestricted
  const { orderMultiple, maxQty } = levels
  return [
    {
      label: "Projected stock at arrival",
      note: `position ${levels.position.toLocaleString()} − sales in transit ${units(sales)} + unrestricted ${unrestricted.toLocaleString()}`,
      value: units(projected),
    },
    {
      label: "Maximum stock",
      note: maximumNote(levels, unrestricted),
      value: maxQty == null ? "None" : maxQty.toLocaleString(),
    },
    {
      label: "Minimum display quantity",
      note: minimumNote(levels, projected),
      value: minDisplayQtyText(levels.mdq),
    },
    {
      label: "Order multiple",
      note: orderMultiple > 1 ? "the qty is a multiple of it" : "no rounding",
      value: orderMultiple.toLocaleString(),
    },
    {
      label: "Unrestricted suggestion",
      note: aboveReorderPoint(levels)
        ? "position above reorder point, so 0"
        : `order-up-to ${levels.orderUpTo.toLocaleString()} − position ${levels.position.toLocaleString()}`,
      value: unrestricted.toLocaleString(),
    },
    {
      operator: change < 0 ? "−" : "+",
      label: "Change due to product settings",
      value: Math.abs(change).toLocaleString(),
    },
    {
      operator: "=",
      label: "Suggested qty",
      value: recommendedQuantity.toLocaleString(),
      total: true,
    },
  ]
}

/** The Unrestricted suggestion breakdown: the gap to the order-up-to level, or 0 above the reorder point. */
export function unrestrictedSuggestionRows({
  engineLevels: levels,
  unrestrictedSuggestion: unrestricted,
}: Pick<ReplRow, "engineLevels" | "unrestrictedSuggestion">): BreakdownRow[] | null {
  if (levels == null || unrestricted == null) return null
  const result: BreakdownRow = {
    operator: "=",
    label: "Unrestricted suggestion",
    value: unrestricted.toLocaleString(),
    total: true,
  }
  if (aboveReorderPoint(levels)) {
    return [
      { label: "Reorder point", value: levels.reorderPoint.toLocaleString() },
      {
        label: "Inventory position",
        note: "above the reorder point, so nothing is needed",
        value: levels.position.toLocaleString(),
      },
      result,
    ]
  }
  return [
    { label: "Order-up-to level", value: levels.orderUpTo.toLocaleString() },
    { operator: "−", label: "Inventory position", value: levels.position.toLocaleString() },
    result,
  ]
}

/** The line under the inspector's suggested qty. */
export function suggestedQtySummary({
  engineLevels: levels,
  unrestrictedSuggestion: unrestricted,
  recommendedQuantity,
}: SuggestedQtyRow): string {
  if (levels == null || unrestricted == null) return "No levels for this line."
  const change = recommendedQuantity - unrestricted
  if (unrestricted === 0 && change === 0) {
    return aboveReorderPoint(levels)
      ? `Position ${levels.position.toLocaleString()} is above the reorder point ${levels.reorderPoint.toLocaleString()}.`
      : `Position ${levels.position.toLocaleString()} already reaches the order-up-to level ${levels.orderUpTo.toLocaleString()}.`
  }
  const base = `Unrestricted suggestion ${unrestricted.toLocaleString()}`
  if (change === 0) return `${base}, unchanged by the product settings.`
  return `${base}, ${signed(change)} from the product settings.`
}
